import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/controls'
import { Modal } from '../ui/modal'
import { Notice, Spinner } from './LegalPresentation'
import { toast as message } from 'sonner'
import { useLocation, useNavigate } from 'react-router-dom'
import { getMerchant } from '../../services/merchant'
import {
  acceptMerchantAgreement,
  getCurrentMerchantAgreement,
  getMerchantAgreementStatus,
  type MerchantAgreementDocument,
  type MerchantAgreementStatus,
} from '../../services/merchantAgreement'
import { ApiError } from '../../services/api'
import { useMerchantStore } from '../../stores/merchantStore'
import MerchantAgreementDocumentView from './MerchantAgreementDocumentView'

const STALE_RENDER_CODES = new Set([40911, 40912, 40913])
const MAX_TIMER_DELAY_MS = 2_147_483_647
const DEADLINE_REFRESH_BUFFER_MS = 25
const PAST_DEADLINE_RETRY_MS = 1_000
const SAFE_OPERATION_ROUTES = [
  '/dashboard/orders',
  '/dashboard/disputes',
  '/dashboard/balance',
  '/dashboard/payouts',
  '/dashboard/bank-accounts',
]

interface MerchantAgreementGateProps {
  children: ReactNode
  urgentAnnouncementActive: boolean
  onAgreementDialogsClosed: () => void
  onDialogOpen?: () => void
}

function isSafeOperationRoute(pathname: string) {
  return SAFE_OPERATION_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  )
}

function formatHongKongDeadline(timestamp: number) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(timestamp)
  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  )
  return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}（香港時間）`
}

export default function MerchantAgreementGate({
  children,
  urgentAnnouncementActive,
  onAgreementDialogsClosed,
  onDialogOpen,
}: MerchantAgreementGateProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const setMerchant = useMerchantStore((state) => state.setMerchant)
  const [status, setStatus] = useState<MerchantAgreementStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusFailed, setStatusFailed] = useState(false)
  const [document, setDocument] = useState<MerchantAgreementDocument | null>(
    null,
  )
  const [signingOpen, setSigningOpen] = useState(false)
  const [documentLoading, setDocumentLoading] = useState(false)
  const [documentFailed, setDocumentFailed] = useState(false)
  const [consented, setConsented] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const inFlightStatusRefresh = useRef<Promise<MerchantAgreementStatus> | null>(
    null,
  )
  const statusRequestSequence = useRef(0)
  const blockingDialogOpen = useRef(false)
  const signingDialogVisible = useRef(false)
  const urgentAnnouncementActiveRef = useRef(urgentAnnouncementActive)
  urgentAnnouncementActiveRef.current = urgentAnnouncementActive

  const refreshStatus = useCallback(
    (force = false): Promise<MerchantAgreementStatus> => {
      if (!force && inFlightStatusRefresh.current)
        return inFlightStatusRefresh.current

      const requestSequence = ++statusRequestSequence.current
      setStatusLoading(true)
      const request = getMerchantAgreementStatus()
        .then((response) => {
          if (requestSequence === statusRequestSequence.current) {
            setStatus(response.data)
            setStatusFailed(false)
          }
          return response.data
        })
        .catch((error: unknown) => {
          if (requestSequence === statusRequestSequence.current)
            setStatusFailed(true)
          throw error
        })
        .finally(() => {
          if (requestSequence === statusRequestSequence.current) {
            inFlightStatusRefresh.current = null
            setStatusLoading(false)
          }
        })
      inFlightStatusRefresh.current = request
      return request
    },
    [],
  )

  useEffect(() => {
    refreshStatus().catch(() => {})
  }, [refreshStatus])

  useEffect(() => {
    const refreshWhenActive = () => {
      void refreshStatus().catch(() => {})
    }
    const refreshWhenVisible = () => {
      if (window.document.visibilityState === 'visible') refreshWhenActive()
    }

    window.addEventListener('focus', refreshWhenActive)
    window.document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshWhenActive)
      window.document.removeEventListener(
        'visibilitychange',
        refreshWhenVisible,
      )
    }
  }, [refreshStatus])

  useEffect(() => {
    if (
      !status?.requiresAction ||
      status.accepted ||
      status.capabilityBlocked ||
      status.graceEndsAt == null
    ) {
      return
    }

    const remaining = status.graceEndsAt - Date.now()
    const delay =
      remaining > 0
        ? Math.min(remaining + DEADLINE_REFRESH_BUFFER_MS, MAX_TIMER_DELAY_MS)
        : PAST_DEADLINE_RETRY_MS
    let cancelled = false
    let timeout: number
    const refreshAtDeadline = async () => {
      try {
        await refreshStatus()
      } catch {
        if (!cancelled)
          timeout = window.setTimeout(refreshAtDeadline, PAST_DEADLINE_RETRY_MS)
      }
    }
    timeout = window.setTimeout(refreshAtDeadline, delay)
    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [refreshStatus, status])

  const loadDocument = useCallback(async () => {
    setDocumentLoading(true)
    setDocumentFailed(false)
    try {
      const response = await getCurrentMerchantAgreement()
      setDocument(response.data)
    } catch {
      setDocumentFailed(true)
    } finally {
      setDocumentLoading(false)
    }
  }, [])

  const openSigning = () => {
    setConsented(false)
    setSigningOpen(true)
    if (!document) void loadDocument()
  }

  const closeSigning = () => {
    if (!accepting) {
      setSigningOpen(false)
      setConsented(false)
    }
  }

  const acceptAgreement = async () => {
    if (!document || !consented) return
    setAccepting(true)
    try {
      await acceptMerchantAgreement({
        accepted: true,
        agreementVersion: document.agreementVersion,
        effectiveDate: document.effectiveDate,
        renderedDocumentSha256: document.renderedDocumentSha256,
      })

      const [statusResult, merchantResult] = await Promise.allSettled([
        refreshStatus(true),
        getMerchant(),
      ])
      if (statusResult.status === 'rejected') throw statusResult.reason
      if (merchantResult.status === 'fulfilled')
        setMerchant(merchantResult.value.data)
      setSigningOpen(false)
      setConsented(false)
      message.success('協議補簽完成')
    } catch (error: unknown) {
      if (
        error instanceof ApiError &&
        error.code &&
        STALE_RENDER_CODES.has(error.code)
      ) {
        setConsented(false)
        await Promise.allSettled([loadDocument(), refreshStatus()])
        message.warning('協議內容已更新，請重新閱讀並確認')
      } else {
        message.error(
          error instanceof Error ? error.message : '協議接受失敗，請重試',
        )
      }
    } finally {
      setAccepting(false)
    }
  }

  const requiresAction = Boolean(status?.requiresAction && !status.accepted)
  const restrictedPageBlocked = Boolean(
    requiresAction &&
      status?.capabilityBlocked &&
      !isSafeOperationRoute(location.pathname),
  )
  const blockingGateOpen =
    restrictedPageBlocked && !signingOpen && !urgentAnnouncementActive
  const signingDialogOpen = signingOpen && !urgentAnnouncementActive

  useEffect(() => {
    if (blockingGateOpen || signingDialogOpen) onDialogOpen?.()
  }, [blockingGateOpen, signingDialogOpen, onDialogOpen])

  useEffect(() => {
    if (blockingGateOpen) blockingDialogOpen.current = true
  }, [blockingGateOpen])

  useEffect(() => {
    if (signingDialogOpen) signingDialogVisible.current = true
  }, [signingDialogOpen])

  useEffect(() => {
    if (
      urgentAnnouncementActive &&
      !blockingDialogOpen.current &&
      !signingDialogVisible.current
    ) {
      onAgreementDialogsClosed()
    }
  }, [onAgreementDialogsClosed, urgentAnnouncementActive])

  const handleAgreementDialogAfterClose = useCallback(
    (dialog: 'blocking' | 'signing') => {
      if (dialog === 'blocking') blockingDialogOpen.current = false
      else signingDialogVisible.current = false

      if (
        urgentAnnouncementActiveRef.current &&
        !blockingDialogOpen.current &&
        !signingDialogVisible.current
      ) {
        onAgreementDialogsClosed()
      }
    },
    [onAgreementDialogsClosed],
  )

  const banner = useMemo(() => {
    if (!requiresAction || !status) return null

    if (status.approvalBlocked) {
      return {
        type: 'warning' as const,
        message: 'Merchant Services Agreement 尚待補簽',
        description: '補簽後才可繼續審批。既有認證材料和申請狀態不會變更。',
      }
    }
    if (status.capabilityBlocked) {
      return {
        type: 'error' as const,
        message: 'Merchant Services Agreement 補簽已逾期',
        description: '新增交易能力已暫停；退款、争议、餘額結算和提現仍可使用。',
      }
    }
    if (status.graceEndsAt) {
      return {
        type: 'warning' as const,
        message: '請在寬限期內完成 Merchant Services Agreement 補簽',
        description: `截止時間：${formatHongKongDeadline(status.graceEndsAt)}。寬限期內現有能力保持可用。`,
      }
    }
    return {
      type: 'warning' as const,
      message: 'Merchant Services Agreement 尚待補簽',
      description: '請完成補簽，既有認證材料和申請狀態不會變更。',
    }
  }, [requiresAction, status])

  const goTo = (pathname: string) => navigate(pathname)

  return (
    <>
      {banner && (
        <Notice
          tone={banner.type}
          title={banner.message}
          body={banner.description}
          action={
            <Button variant="default" onClick={openSigning}>
              立即簽署
            </Button>
          }
          style={{ marginBottom: 20 }}
        />
      )}

      {statusFailed && (
        <Notice
          tone="error"
          title="無法確認 Merchant Services Agreement 狀態"
          body="請重新檢查協議狀態；狀態恢復前請勿建立新的交易能力。"
          action={
            <Button
              disabled={statusLoading}
              onClick={() => void refreshStatus().catch(() => {})}
            >
              重新檢查協議狀態
            </Button>
          }
          style={{ marginBottom: 20 }}
        />
      )}

      {children}

      <Modal
        open={blockingGateOpen}
        className="max-w-2xl"
        afterClose={() => handleAgreementDialogAfterClose('blocking')}
        onOpenChange={() => {}}
        title="Merchant Services Agreement 補簽要求"
        closable={false}
        footer={null}
      >
        <div className="flex flex-col gap-3">
          <Notice
            tone="error"
            title="新增交易能力已暫停"
            body="完成協議補簽後將立即恢復。退款、争议處理、餘額結算和提現不受影響。"
          />
          <Button variant="default" onClick={openSigning}>
            立即簽署
          </Button>
          <div className="flex flex-col gap-3">
            <Button variant="outline" onClick={() => goTo('/dashboard/orders')}>
              訂單退款
            </Button>
            <Button
              variant="outline"
              onClick={() => goTo('/dashboard/balance/refunds')}
            >
              退款記錄
            </Button>
            <Button
              variant="outline"
              onClick={() => goTo('/dashboard/disputes')}
            >
              争议案件
            </Button>
            <Button
              variant="outline"
              onClick={() => goTo('/dashboard/balance')}
            >
              餘額與結算
            </Button>
            <Button
              variant="outline"
              onClick={() => goTo('/dashboard/payouts')}
            >
              結款與提現
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={signingDialogOpen}
        className="max-w-4xl"
        afterClose={() => handleAgreementDialogAfterClose('signing')}
        title="補簽 Merchant Services Agreement"
        onOpenChange={(value) => {
          if (!value) closeSigning()
        }}
        closable={!accepting}
        footer={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={closeSigning}
              disabled={accepting}
            >
              稍後處理
            </Button>
            <Button
              variant="default"
              disabled={accepting || !document || !consented || documentFailed}
              onClick={acceptAgreement}
            >
              確認並接受協議
            </Button>
          </div>
        }
      >
        {documentLoading ? (
          <div
            style={{
              minHeight: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </div>
        ) : documentFailed || !document ? (
          <Notice
            tone="error"
            title="協議暫時無法載入"
            body={
              <Button variant="link" onClick={() => void loadDocument()}>
                重新載入
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Notice
              tone="info"
              title="合同相對方"
              body={status?.merchantLegalName || '暫時無法確認合同相對方'}
            />
            <div>
              <h3 className="font-semibold" style={{ margin: 0 }}>
                KyrenPay Merchant Services Agreement
              </h3>
              <span className="text-muted-foreground">
                版本 {document.agreementVersion} · Effective Date：
                {document.effectiveDateDisplay}
              </span>
            </div>
            <div
              data-testid="legacy-merchant-agreement-body"
              className="merchant-agreement-scroll"
              style={{ maxHeight: 'min(48vh, 460px)' }}
            >
              <MerchantAgreementDocumentView
                markdownContent={document.markdownContent}
              />
            </div>
            <Checkbox checked={consented} onCheckedChange={setConsented}>
              {document.acceptanceStatement}
            </Checkbox>
          </div>
        )}
      </Modal>
    </>
  )
}
